import * as React from 'react';

import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '../../../../ui/icon/icon';
import type { CommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import { CommentAvatar } from './comment-byline';

/** Roughly six lines before the box stops growing and starts scrolling. */
const MAX_HEIGHT_PX = 132;

export interface CommentComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  placeholder: string;
  /** Accessible name for the send button, e.g. "Add comment" or "Send reply". */
  submitLabel: string;
  /** Whoever is about to write. Omitted, the box has no avatar. */
  author?: CommentAuthor | null;
  /**
   * `card` is the box standing on its own, over the prose. `plain` is the same box
   * already inside one — a thread's card, the foot of the rail — where its own border
   * would frame a frame. The parent's rule separates it there instead.
   */
  chrome?: 'card' | 'plain';
  autoFocus?: boolean;
  className?: string;
}

/**
 * The one box anything is written into — a new comment, a reply to one.
 *
 * Starts as a single row: avatar, prompt, send. It only becomes a block once there is
 * something in it, at which point the text takes the full width and send drops to a
 * line of its own. An empty comment box is a small thing sitting in the margin of
 * someone else's document, and it should ask for as little room as it can until it is
 * being used.
 *
 * That growth is a class swap on one element rather than two layouts behind a
 * condition: `basis-full` on the textarea is what pushes the buttons onto the next
 * flex line. Rendering a different tree on the first keystroke would rebuild the
 * textarea and drop the caret out of the box the user is typing in.
 *
 * Send is a round icon button with no worded twin, and there is no Cancel: the way out
 * is to click somewhere else, which is what the caller wires up. Enter sends,
 * Shift+Enter breaks the line.
 */
export function CommentComposer({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder,
  submitLabel,
  author = null,
  chrome = 'card',
  autoFocus = false,
  className,
}: CommentComposerProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (!autoFocus) {
      return;
    }
    // A frame late: the popover is positioned by floating-ui on mount, and focusing
    // before it lands makes the browser scroll to where it used to be.
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [autoFocus]);

  // Grow with the text rather than scrolling inside a fixed two rows. Measured before
  // paint, and from `auto` each time, because `scrollHeight` on an element already
  // tall enough for its content only ever reports the height it was last given.
  React.useLayoutEffect(() => {
    const node = textareaRef.current;
    if (node === null) {
      return;
    }
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  const hasContent = value !== '';
  const canSubmit = value.trim() !== '';

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 p-3 transition-colors',
        chrome === 'card' &&
          'rounded-xl border border-border bg-popover focus-within:border-muted-foreground/40',
        className
      )}
    >
      {author !== null ? (
        // Centred against the first line, not the top of the row. The row is as tall
        // as the send button beside it, and the text is centred in that — so an
        // avatar simply pinned to the top sits a few pixels above the words it is
        // supposed to be level with. `h-7` is the button's height: matching it puts
        // the avatar on the same line, and keeps it there when the text wraps and
        // the row grows beneath it.
        <div className="flex h-7 shrink-0 items-center">
          <CommentAvatar name={author.name} />
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-y-1.5">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Mid-composition, Enter belongs to the input method: it is how a
              // Japanese or Chinese typist accepts the candidate they are choosing,
              // and sending on it would post a half-written word.
              if (event.nativeEvent.isComposing) {
                return;
              }
              // Shift holds the line break, the way every message box does. The
              // modifier combinations keep working for the muscle memory that had to
              // use them before.
              if (!event.shiftKey) {
                event.preventDefault();
                onSubmit();
              }
              return;
            }
            if (event.key === 'Escape' && onCancel !== undefined) {
              event.preventDefault();
              onCancel();
            }
          }}
          placeholder={placeholder}
          rows={1}
          className={cn(
            'min-w-0 resize-none self-center overflow-y-auto bg-transparent text-sm leading-6 outline-none placeholder:text-muted-foreground',
            // Full width once there is something to read, which wraps the send
            // button onto its own line beneath it.
            hasContent ? 'basis-full' : 'flex-1'
          )}
        />
        <Button
          type="button"
          variant="default"
          size="sm"
          aria-label={submitLabel}
          title={submitLabel}
          className={cn(
            'size-7 shrink-0 rounded-full p-0',
            hasContent && 'ml-auto'
          )}
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          <Icon icon="arrow-up" className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
